/**
 * Activity tab (Phase 5 Action 49).
 *
 * Flat timeline of the last 100 events for this tenant. Events sharing a
 * correlation_id (e.g., operator action → tenant.activated → downstream
 * effects) render as an indented group under the first event in the
 * cluster, so an investigator can eyeball "which of these were one
 * logical operation."
 *
 * Payload is rendered as a collapsible JSON blob. Ugly on purpose —
 * this is an operator-facing debug view, not customer UX. If we ever
 * want a prettier per-event-type rendering, key off `eventType` and
 * pull a formatter from a registry.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import { fetchJson, formatDateTime, type TenantContext } from './shared';

interface EventRow {
  eventId: string;
  eventType: string;
  occurredAt: string;
  actorType: string;
  actorId: string | null;
  correlationId: string | null;
  payload: Record<string, unknown>;
}

interface Cluster {
  key: string;              // correlationId or eventId for solo events
  events: EventRow[];       // newest first within the cluster
  correlationId: string | null;
}

const ACTOR_BADGE: Record<string, string> = {
  user:      'bg-slate-100 text-slate-700',
  admin:     'bg-primary-100 text-primary-800',
  operator:  'bg-amber-100 text-amber-800',
  system:    'bg-slate-100 text-slate-700',
  slack_bot: 'bg-emerald-100 text-emerald-800',
  cron:      'bg-slate-100 text-slate-700',
};

// Groups events by correlation_id while preserving overall time order.
// Solo events (correlation_id NULL) each get their own single-element
// cluster so the render loop stays uniform.
function groupByCorrelation(events: EventRow[]): Cluster[] {
  const clusters: Cluster[] = [];
  const seenCorr = new Map<string, Cluster>();
  for (const e of events) {
    if (!e.correlationId) {
      clusters.push({ key: e.eventId, events: [e], correlationId: null });
      continue;
    }
    const existing = seenCorr.get(e.correlationId);
    if (existing) {
      existing.events.push(e);
    } else {
      const cluster: Cluster = { key: e.correlationId, events: [e], correlationId: e.correlationId };
      seenCorr.set(e.correlationId, cluster);
      clusters.push(cluster);
    }
  }
  return clusters;
}

export default function Activity() {
  const { tenant } = useOutletContext<TenantContext>();

  const q = useQuery({
    queryKey: ['tenant-activity', tenant.id],
    queryFn: () => fetchJson<{ data: { events: EventRow[] } }>(`/api/tenants/${tenant.id}/activity`),
  });

  const clusters = useMemo<Cluster[]>(() => {
    if (!q.data) return [];
    return groupByCorrelation(q.data.data.events);
  }, [q.data]);

  if (q.isLoading) {
    return <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading activity…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Could not load activity: {q.error instanceof Error ? q.error.message : 'unknown error'}
      </div>
    );
  }

  const totalEvents = q.data.data.events.length;

  return (
    <section className="mb-6 rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Recent activity</h2>
        <span className="text-xs text-slate-500">
          {totalEvents} event{totalEvents === 1 ? '' : 's'} in {clusters.length} group{clusters.length === 1 ? '' : 's'}
        </span>
      </header>
      {totalEvents === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-slate-500">
          No activity recorded for this tenant yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {clusters.map((cluster) => (
            <ClusterRow key={cluster.key} cluster={cluster} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ClusterRow({ cluster }: { cluster: Cluster }) {
  return (
    <li className="px-6 py-3">
      {cluster.correlationId && cluster.events.length > 1 && (
        <div className="mb-1 text-xs text-slate-400 font-mono">
          correlation {cluster.correlationId.slice(0, 8)} · {cluster.events.length} events
        </div>
      )}
      <ul className={clsx('space-y-2', cluster.events.length > 1 && 'border-l-2 border-slate-200 pl-3')}>
        {cluster.events.map((e) => <EventRowView key={e.eventId} event={e} />)}
      </ul>
    </li>
  );
}

function EventRowView({ event }: { event: EventRow }) {
  const [open, setOpen] = useState(false);
  const hasPayload = event.payload && Object.keys(event.payload).length > 0;
  return (
    <li className="text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-slate-900">{event.eventType}</span>
        <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', ACTOR_BADGE[event.actorType] ?? 'bg-slate-100 text-slate-700')}>
          {event.actorType}
        </span>
        <span className="ml-auto text-xs text-slate-500">{formatDateTime(event.occurredAt)}</span>
      </div>
      {hasPayload && (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            {open ? 'Hide payload' : 'Show payload'}
          </button>
          {open && (
            <pre className="mt-1 overflow-x-auto rounded-md bg-slate-50 p-2 text-xs text-slate-700 font-mono">
              {JSON.stringify(event.payload, null, 2)}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}
